import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { HighlightStroke } from "@tebiki/shared";

/**
 * Highlighter — the cross-OS "蛍光ペン" surface. An <img> with an <svg> overlay; freehand
 * strokes are captured with **Pointer Events** so mouse / touch / pen (Apple Pencil) are all
 * handled by one code path. Coordinates are normalised to a 0..1000 box (resolution-independent,
 * matches the @tebiki/shared contract), so a saved mark replays identically on any device.
 *
 * Brand feel: lime ink with `mix-blend-mode:multiply` + low opacity → a real highlighter wash
 * (the paper/photo shows through). Round caps/joins give the soft felt-tip edge.
 *
 * The container sets `touch-action:none` so a drag never scrolls/zooms the page mid-stroke.
 */

/** Default brand highlighter lime (var(--hi)). */
export const HI_DEFAULT = "var(--hi)";

/** A small, friendly palette of highlighter inks (label + CSS colour). */
export const HI_PALETTE: { label: string; color: string }[] = [
  { label: "ライム", color: "var(--hi)" },
  { label: "イエロー", color: "#FFE14D" },
  { label: "ピンク", color: "#FF8FB1" },
  { label: "ブルー", color: "#7FD4FF" },
  { label: "オレンジ", color: "#FFB258" },
];

/** Default stroke width in normalised (0..1000) units. ~38 ≈ a fat marker swipe. */
export const HI_WIDTH_DEFAULT = 38;

export type HighlightTool = "draw" | "erase";

interface HighlighterProps {
  /** image url (already resolved via mediaUrl). When null a neutral placeholder is shown. */
  src?: string | null;
  alt?: string;
  /** the strokes to render / edit. */
  value: HighlightStroke[];
  /** edit callback; omit (or set readOnly) for a display-only overlay (Viewer/Preview/Digest). */
  onChange?: (next: HighlightStroke[]) => void;
  /** display-only: render the marks but capture no pointer input. */
  readOnly?: boolean;
  /** active ink colour for new strokes (defaults to brand lime). */
  color?: string;
  /** active stroke width (0..1000 units) for new strokes. */
  width?: number;
  /** active tool — draw (default) or erase (tap a stroke to remove it). */
  tool?: HighlightTool;
  style?: CSSProperties;
  className?: string;
}

/** The SVG view box edge length: every coordinate lives in 0..1000. */
const VB = 1000;

/** Convert a flat points array [x0,y0,x1,y1,…] into an SVG polyline `points` string. */
function toPolyPoints(points: number[]): string {
  let out = "";
  for (let i = 0; i + 1 < points.length; i += 2) {
    out += `${points[i]},${points[i + 1]} `;
  }
  return out.trim();
}

const clamp1000 = (n: number) => Math.max(0, Math.min(VB, n));

/**
 * Squared distance from point p to segment a-b, all in the 0..1000 space. Used by the eraser
 * to decide which stroke a tap landed on.
 */
function distSqToSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
}

/** Index of the stroke nearest to (x,y) within its own width, or -1 if none is close enough. */
function hitStroke(strokes: HighlightStroke[], x: number, y: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let s = 0; s < strokes.length; s++) {
    const pts = strokes[s].points;
    const w = strokes[s].width ?? HI_WIDTH_DEFAULT;
    // a tap within ~half the stroke width (plus a small slop) counts as a hit
    const tol = Math.max(w / 2 + 12, 24);
    const tol2 = tol * tol;
    if (pts.length === 2) {
      const d = (pts[0] - x) ** 2 + (pts[1] - y) ** 2;
      if (d < tol2 && d < bestD) {
        bestD = d;
        best = s;
      }
      continue;
    }
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const d = distSqToSeg(x, y, pts[i], pts[i + 1], pts[i + 2], pts[i + 3]);
      if (d < tol2 && d < bestD) {
        bestD = d;
        best = s;
      }
    }
  }
  return best;
}

function StrokePoly({ stroke, idx }: { stroke: HighlightStroke; idx: number }) {
  return (
    <polyline
      key={idx}
      points={toPolyPoints(stroke.points)}
      fill="none"
      stroke={stroke.color ?? HI_DEFAULT}
      strokeWidth={stroke.width ?? HI_WIDTH_DEFAULT}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

export function Highlighter({
  src,
  alt = "",
  value,
  onChange,
  readOnly = false,
  color = HI_DEFAULT,
  width = HI_WIDTH_DEFAULT,
  tool = "draw",
  style,
  className,
}: HighlighterProps) {
  const editable = !readOnly && !!onChange;
  const svgRef = useRef<SVGSVGElement>(null);
  const gradId = useId();
  // When this component renders its OWN image (editable stage), adopt the image's natural aspect
  // ratio so a landscape photo isn't cropped and the drawn strokes map 1:1 onto the pixels.
  const [ratio, setRatio] = useState<number | null>(null);

  // The stroke currently being drawn (null when idle). Kept in state so it renders live.
  const [draft, setDraft] = useState<number[] | null>(null);
  const drawingRef = useRef(false);

  // Map a pointer event to a normalised 0..1000 coordinate within the SVG viewport.
  const toLocal = useCallback((clientX: number, clientY: number): [number, number] => {
    const el = svgRef.current;
    if (!el) return [0, 0];
    const b = el.getBoundingClientRect();
    if (b.width === 0 || b.height === 0) return [0, 0];
    const x = clamp1000(((clientX - b.left) / b.width) * VB);
    const y = clamp1000(((clientY - b.top) / b.height) * VB);
    return [x, y];
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!editable) return;
      e.preventDefault();
      const [x, y] = toLocal(e.clientX, e.clientY);
      if (tool === "erase") {
        const hit = hitStroke(value, x, y);
        if (hit >= 0 && onChange) onChange(value.filter((_, i) => i !== hit));
        return;
      }
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* setPointerCapture can throw if the pointer is already gone — safe to ignore */
      }
      drawingRef.current = true;
      setDraft([x, y]);
    },
    [editable, tool, toLocal, value, onChange],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!editable || !drawingRef.current) return;
      e.preventDefault();
      const [x, y] = toLocal(e.clientX, e.clientY);
      setDraft((prev) => {
        if (!prev) return [x, y];
        const last = prev.length;
        // skip near-duplicate samples so the points array stays compact
        if (last >= 2 && Math.abs(prev[last - 2] - x) < 3 && Math.abs(prev[last - 1] - y) < 3) {
          return prev;
        }
        return [...prev, x, y];
      });
    },
    [editable, toLocal],
  );

  const finishStroke = useCallback(
    (e?: React.PointerEvent<SVGSVGElement>) => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      if (e) {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* already released */
        }
      }
      setDraft((prev) => {
        // A tap (single point) becomes a tiny dab so it's still visible & erasable.
        const pts = prev && prev.length >= 2 ? (prev.length === 2 ? [prev[0], prev[1], prev[0] + 1, prev[1]] : prev) : null;
        if (pts && onChange) {
          onChange([...value, { points: pts, color, width }]);
        }
        return null;
      });
    },
    [onChange, value, color, width],
  );

  // Safety net: if a pointerup/cancel is missed (e.g. pointer leaves the window), end the stroke.
  useEffect(() => {
    if (!editable) return;
    const end = () => {
      if (drawingRef.current) {
        drawingRef.current = false;
        setDraft((prev) => {
          const pts =
            prev && prev.length >= 2
              ? prev.length === 2
                ? [prev[0], prev[1], prev[0] + 1, prev[1]]
                : prev
              : null;
          if (pts && onChange) onChange([...value, { points: pts, color, width }]);
          return null;
        });
      }
    };
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [editable, onChange, value, color, width]);

  return (
    <div
      className={["hi-stage", className].filter(Boolean).join(" ")}
      style={{ position: "relative", touchAction: editable ? "none" : undefined, ...style, ...(src && ratio ? { aspectRatio: String(ratio) } : null) }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth > 0 && im.naturalHeight > 0) setRatio(im.naturalWidth / im.naturalHeight);
          }}
          style={{ display: "block", width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      ) : !readOnly ? (
        // editable + no image: show a neutral canvas to draw on. In read-only mode we render
        // nothing here so the overlay sits transparently on top of a parent's own image.
        <div
          aria-hidden="true"
          style={{
            width: "100%",
            height: "100%",
            background: "radial-gradient(120% 90% at 60% 40%,#2a2f3a,#0e1014)",
          }}
        />
      ) : null}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB} ${VB}`}
        preserveAspectRatio="none"
        role={editable ? "application" : "img"}
        aria-label={editable ? "蛍光ペンで画像にマーク" : undefined}
        aria-hidden={editable ? undefined : true}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          // multiply blend = the highlighter wash; the photo shows through the ink.
          mixBlendMode: "multiply",
          opacity: 0.45,
          touchAction: editable ? "none" : undefined,
          cursor: editable ? (tool === "erase" ? "pointer" : "crosshair") : "default",
          pointerEvents: editable ? "auto" : "none",
        }}
      >
        {/* unused gradient keeps a stable id namespace if future inks need it */}
        <defs>
          <linearGradient id={gradId} />
        </defs>
        {value.map((s, i) => (
          <StrokePoly key={i} stroke={s} idx={i} />
        ))}
        {draft && draft.length >= 2 ? (
          <polyline
            points={toPolyPoints(draft)}
            fill="none"
            stroke={color}
            strokeWidth={width}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
      </svg>
    </div>
  );
}
